import { z } from 'zod';
import { format, subDays } from 'date-fns';
import {
  MAX_PRICELIST_LOG_EXPORT_CASE_IDS,
  PricelistCaseSortBySchema,
  thenSortSchema,
  type ExportPricelistLogInput,
  type PricelistCaseListFilters,
  type PricelistCaseSortBy,
} from '@erp/rpc';

/**
 * Pure state for the pricelist cases list — the URL search shape, the
 * per-user saved-filter shape, and the two projections everything else is
 * built from. React-free so it stays trivially unit-testable; mirrors
 * `collectionThreadFilterPrefs` for the AR threads table, which is the
 * precedent this list follows.
 */

/** Stable key for this table's saved-filter row in `core_user_table_filter_configs`. */
export const PRICELIST_CASES_TABLE_ID = 'im-pricelist-cases';

/** `YYYY-MM-DD`, as a native date input gives it. */
const DateOnlyParamSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/**
 * The route search params this view owns, all `pc`-prefixed so they cannot
 * collide with the sibling tabs' state on the same route. Every key is
 * optional: the URL is the shareable source of truth, and an absent key
 * means "the default".
 */
export const PricelistCasesSearchSchema = z.object({
  pcSearch: z.string().optional(),
  pcCompanyId: z.string().uuid().optional(),
  pcAssignees: z.array(z.string()).optional(),
  pcWaitingOn: z.array(z.string()).optional(),
  pcEffectiveFrom: DateOnlyParamSchema.optional(),
  pcEffectiveTo: DateOnlyParamSchema.optional(),
  pcReceivedFrom: DateOnlyParamSchema.optional(),
  pcReceivedTo: DateOnlyParamSchema.optional(),
  pcOpenedFrom: DateOnlyParamSchema.optional(),
  pcOpenedTo: DateOnlyParamSchema.optional(),
  /** Only Ship & Debit vendors' cases; absent means no such filter. */
  pcShipAndDebit: z.boolean().optional(),
  pcSortBy: PricelistCaseSortBySchema.optional(),
  pcSortDirection: z.enum(['asc', 'desc']).optional(),
  /** Tie-breaking sort levels after `pcSortBy`; a stale one degrades to none. */
  pcThenSort: thenSortSchema(PricelistCaseSortBySchema)
    .optional()
    .catch(undefined),
  pcPage: z.coerce.number().int().min(1).optional(),
  pcPageSize: z.coerce.number().int().min(1).max(200).optional(),
});
export type PricelistCasesSearch = z.infer<typeof PricelistCasesSearchSchema>;

/**
 * What is remembered per user across sessions: the filters, the sort and the
 * page size. The page itself is deliberately not — landing someone on page 5
 * with no context is worse than page 1. Module scope so the reference is
 * stable for `useTableFilterPreferences`' parse cache.
 */
export const PricelistCaseFilterPrefsSchema = PricelistCasesSearchSchema.omit({
  pcPage: true,
});
export type PricelistCaseFilterPrefs = z.infer<
  typeof PricelistCaseFilterPrefsSchema
>;

export const PERSISTED_CASE_FILTER_KEYS = [
  'pcSearch',
  'pcCompanyId',
  'pcAssignees',
  'pcWaitingOn',
  'pcEffectiveFrom',
  'pcEffectiveTo',
  'pcReceivedFrom',
  'pcReceivedTo',
  'pcOpenedFrom',
  'pcOpenedTo',
  'pcShipAndDebit',
  'pcSortBy',
  'pcSortDirection',
  'pcThenSort',
  'pcPageSize',
] as const satisfies readonly (keyof PricelistCaseFilterPrefs)[];

/**
 * The keys that are FILTERS — what "Clear filters" resets. The sort and the
 * page size are view state and survive a clear.
 */
export const CLEARABLE_CASE_FILTER_KEYS = [
  'pcSearch',
  'pcCompanyId',
  'pcAssignees',
  'pcWaitingOn',
  'pcEffectiveFrom',
  'pcEffectiveTo',
  'pcReceivedFrom',
  'pcReceivedTo',
  'pcOpenedFrom',
  'pcOpenedTo',
  'pcShipAndDebit',
] as const satisfies readonly (keyof PricelistCaseFilterPrefs)[];

/**
 * What a brand-new user lands on: everything, newest first. No hidden lens —
 * a default that quietly drops older cases reads as "where did they go?" —
 * so narrowing is always a visible filter (see {@link recentOpenedFrom}).
 */
export const DEFAULT_CASE_FILTER_PREFS: PricelistCaseFilterPrefs = {
  pcSortBy: 'createdAt',
  pcSortDirection: 'desc',
};

/** How far back the "Recent" quick filter reaches (GEN-2580: the prior week). */
export const RECENT_OPENED_DAYS = 7;

/**
 * The "Recent" quick filter as a plain opened-date filter: opened on or after
 * this day (local calendar), open-ended. It is one preset of the Opened range
 * picker, so it shows up in the picker, clears with the other filters and
 * never hides anything a user cannot see the reason for.
 *
 * The boundary is inclusive, so "the last 7 days" is today plus the six
 * before it — hence the minus one.
 */
export function recentOpenedFrom(now: Date): string {
  return format(subDays(now, RECENT_OPENED_DAYS - 1), 'yyyy-MM-dd');
}

/** True when the Opened range is exactly the Recent preset for `now`. */
export function isRecentQuickFilter(
  params: Partial<PricelistCasesSearch>,
  now: Date,
): boolean {
  return (
    params.pcOpenedFrom === recentOpenedFrom(now) &&
    params.pcOpenedTo === undefined
  );
}

export const DEFAULT_CASE_PAGE_SIZE = 25;

/**
 * Project the search params down to the persisted shape in a fixed key order
 * with empties dropped, so `JSON.stringify` is a reliable equality check when
 * deciding whether the saved row actually changed.
 */
export function buildCaseFilterPrefs(
  params: Partial<PricelistCaseFilterPrefs>,
): PricelistCaseFilterPrefs {
  const prefs: PricelistCaseFilterPrefs = {};
  if (params.pcSearch) prefs.pcSearch = params.pcSearch;
  if (params.pcCompanyId) prefs.pcCompanyId = params.pcCompanyId;
  if (params.pcAssignees?.length) prefs.pcAssignees = params.pcAssignees;
  if (params.pcWaitingOn?.length) prefs.pcWaitingOn = params.pcWaitingOn;
  if (params.pcEffectiveFrom) prefs.pcEffectiveFrom = params.pcEffectiveFrom;
  if (params.pcEffectiveTo) prefs.pcEffectiveTo = params.pcEffectiveTo;
  if (params.pcReceivedFrom) prefs.pcReceivedFrom = params.pcReceivedFrom;
  if (params.pcReceivedTo) prefs.pcReceivedTo = params.pcReceivedTo;
  if (params.pcOpenedFrom) prefs.pcOpenedFrom = params.pcOpenedFrom;
  if (params.pcOpenedTo) prefs.pcOpenedTo = params.pcOpenedTo;
  if (params.pcShipAndDebit) prefs.pcShipAndDebit = true;
  if (params.pcSortBy) prefs.pcSortBy = params.pcSortBy;
  if (params.pcSortDirection) prefs.pcSortDirection = params.pcSortDirection;
  if (params.pcThenSort?.length) prefs.pcThenSort = params.pcThenSort;
  if (params.pcPageSize) prefs.pcPageSize = params.pcPageSize;
  return prefs;
}

/**
 * The search params as the contract's shared filter shape — the SAME object
 * the list query sends and a select-all export sends, so the two cannot
 * disagree about what "everything matching the active filter" means.
 */
export function toCaseListFilters(
  params: Partial<PricelistCasesSearch>,
): PricelistCaseListFilters {
  return {
    search: params.pcSearch || undefined,
    companyId: params.pcCompanyId,
    assignees: params.pcAssignees?.length ? params.pcAssignees : undefined,
    waitingOn: params.pcWaitingOn?.length ? params.pcWaitingOn : undefined,
    effectiveFrom: params.pcEffectiveFrom,
    effectiveTo: params.pcEffectiveTo,
    receivedFrom: params.pcReceivedFrom,
    receivedTo: params.pcReceivedTo,
    openedFrom: params.pcOpenedFrom,
    openedTo: params.pcOpenedTo,
    shipAndDebit: params.pcShipAndDebit ? true : undefined,
  };
}

/** True when any clearable filter is set. */
export function hasCaseFilters(params: Partial<PricelistCasesSearch>): boolean {
  return CLEARABLE_CASE_FILTER_KEYS.some((key) => {
    const value = params[key];
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  });
}

/** Column ids that round-trip to the contract's `sortBy`; anything else is unsortable. */
export function toCaseSortBy(
  id: string | undefined,
): PricelistCaseSortBy | undefined {
  const parsed = PricelistCaseSortBySchema.safeParse(id);
  return parsed.success ? parsed.data : undefined;
}

/** What the list has selected for a bulk action. */
export interface CaseSelection {
  /** Ticked row ids on the current page. */
  ids: string[];
  /**
   * The user escalated a full-page tick to "every case matching the filter",
   * which the page's ids cannot represent — a select-all over 400 cases with
   * 25 on screen must not quietly export 25.
   */
  allMatching: boolean;
}

/**
 * The export input for a selection, or null when there is nothing exportable.
 *
 * Explicit ticks send `mode: "ids"` (capped — the ids ride in a GET URL);
 * "all matching" sends the FILTER, never the page's ids. The over-cap case is
 * null rather than a truncated list, so the button disables and explains
 * instead of exporting a subset with no warning.
 */
export function exportInputForSelection(
  selection: CaseSelection,
  filters: PricelistCaseListFilters,
): ExportPricelistLogInput | null {
  if (selection.allMatching) return { mode: 'filter', ...filters };
  if (
    selection.ids.length === 0 ||
    selection.ids.length > MAX_PRICELIST_LOG_EXPORT_CASE_IDS
  ) {
    return null;
  }
  return { mode: 'ids', caseIds: selection.ids };
}
